/**
 * Turning a pasted address into fields.
 *
 * Nobody types an address into five boxes if they already have it on their
 * clipboard. This takes what they pasted — off a work order, a text message,
 * a maps app — and works out which part is which.
 *
 * It is deliberately conservative. A field it is not sure about is left empty
 * for a person to fill in, because a wrong city is worse than a blank one: the
 * blank one gets noticed. Everything it does know comes from the shape of US
 * addresses, so it never needs a network call, a key or a bill.
 *
 * This lives in shared because the web app's customer form has the same paste
 * to deal with, and two parsers would eventually disagree about the same
 * clipboard.
 */
/** Empty strings rather than nulls: these go straight into text inputs. */
const EMPTY = { line1: '', line2: '', city: '', state: '', postalCode: '' };
/** ZIP or ZIP+4. */
const ZIP = /^\d{5}(?:-\d{4})?$/;
/** A two-letter state, on its own or followed by a ZIP. */
const STATE_ZIP = /^([A-Za-z]{2})[\s,]+(\d{5}(?:-\d{4})?)$/;
/** "Apt 3", "Unit B", "#14", "Suite 200" — the part that is not the street. */
const SECONDARY = /^(?:apt\.?|apartment|unit|suite|ste\.?|#|fl\.?|floor|rm\.?|room|bldg\.?|building)\b/i;
const COUNTRY = /^(?:usa|u\.s\.a\.?|us|united states(?: of america)?)$/i;
/**
 * True when the text looks like more than one field's worth of address — the
 * signal for offering to split it, rather than splitting as somebody types.
 */
export function looksLikeFullAddress(text) {
    const parsed = parseAddress(text);
    // A ZIP, or a city and state together. One of those on its own is just a
    // line somebody is halfway through typing.
    return Boolean(parsed.postalCode || (parsed.city && parsed.state));
}
export function parseAddress(input) {
    // Collapse runs of spaces and tabs but keep newlines: people paste
    // addresses one line per part, and those line breaks are the separators.
    const raw = (input ?? '').replace(/[^\S\n]+/g, ' ').trim();
    if (!raw)
        return { ...EMPTY };
    // Split on commas or newlines; people paste both.
    let parts = raw
        .split(/[,\n]/)
        .map((part) => part.trim())
        .filter(Boolean);
    // A trailing country tells us nothing we do not already assume.
    while (parts.length > 1 && COUNTRY.test(parts[parts.length - 1]))
        parts.pop();
    if (parts.length === 0)
        return { ...EMPTY };
    const out = { ...EMPTY };
    /*
     * Read from the end, because the end of a US address is the predictable
     * part. Whatever is left over at the front is the street, which is the one
     * field that genuinely cannot be pattern-matched.
     */
    let tail = parts[parts.length - 1];
    const stateZip = STATE_ZIP.exec(tail);
    if (stateZip) {
        out.state = stateZip[1].toUpperCase();
        out.postalCode = stateZip[2];
        parts.pop();
    }
    else if (ZIP.test(tail)) {
        out.postalCode = tail;
        parts.pop();
        // "Austin, TX, 78704" — the state was its own comma-separated part.
        tail = parts[parts.length - 1] ?? '';
        if (/^[A-Za-z]{2}$/.test(tail)) {
            out.state = tail.toUpperCase();
            parts.pop();
        }
    }
    else if (/^[A-Za-z]{2}$/.test(tail)) {
        out.state = tail.toUpperCase();
        parts.pop();
    }
    else {
        /*
         * No commas before the state — "1420 Maple Ridge Rd Austin TX 78704".
         * The ZIP and state can still be lifted off the end by shape. The city
         * cannot: "West Hills" and "Hills" are equally plausible, and guessing
         * wrong silently is the failure this parser exists to avoid. So the
         * remainder stays in the street line for a person to split by eye.
         */
        const loose = /^(.*?)[\s,]+([A-Za-z]{2})[\s,]+(\d{5}(?:-\d{4})?)$/.exec(tail);
        if (loose && parts.length === 1) {
            out.state = loose[2].toUpperCase();
            out.postalCode = loose[3];
            out.line1 = loose[1].trim();
            return out;
        }
    }
    if (parts.length === 0)
        return out;
    // What is left: the last part is the city, the rest is the street.
    if (parts.length >= 2) {
        out.city = parts[parts.length - 1];
        parts = parts.slice(0, -1);
    }
    /*
     * A unit number is its own field, so pull it out when it announces itself.
     * "1420 Maple Ridge Rd, Apt 3, Austin, TX" leaves two street parts here.
     */
    const secondary = parts.findIndex((part) => SECONDARY.test(part));
    if (secondary > 0) {
        out.line2 = parts[secondary];
        parts = parts.filter((_, i) => i !== secondary);
    }
    out.line1 = parts.join(', ');
    /*
     * One part and nothing else identified means it was a street on its own —
     * which is what the field already held, so nothing was learned.
     */
    if (!out.city && !out.state && !out.postalCode)
        return { ...EMPTY, line1: out.line1 };
    return out;
}
//# sourceMappingURL=address-parse.js.map